import React, { FC } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { observable } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { employeeLegalEntity, employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'
import users from 'modules/core/store/collections/users'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { FixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import { ContractFlowProvider } from 'modules/contracts/lib/contractFlowContext'
import { ContractVersionsProvider } from 'modules/contracts/lib/contractVersionsContext'
import { LegalEntityContractDataProvider } from 'modules/contracts/lib/legalEntityContractDataContext'
import {
  filterContractVersionsByCurrent,
  getUpcomingContractVersions,
} from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import contractVersionsPayrollPolicies, {
  ContractVersionsPayrollPoliciesProvider,
} from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import { EmployeePersonalProvider } from 'modules/employees/components/blocks/EmployeePersonalProvider'
import useEmployeeIncidenceRules from 'modules/employees/hooks/useEmployeeIncidenceRules'
import jobSalaryRangesCollection from 'modules/job_catalog/store/collections/salaryRanges'
import policies, { PoliciesProvider } from 'modules/payroll_bundle/store/collections/policies'
import { DataCollectionCategories } from 'modules/processes/domain'

import { TaskDetail } from '../../TaskElement/useTaskDetail'
import UploadDocuments from '../UploadDocuments'
import ContractData from './ContractData'
import EmployeeData from './EmployeeData'
import Footer from './Footer'
import { IDAndSocialSecurityUploads } from './IDAndSocialSecurityUploads'
import Warning from './Warning'

interface Props {
  task: TaskDetail
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  canEmployeeManageAnyEmployeeFolder: boolean
}

interface ICategory {
  category: DataCollectionCategories
  configuration: {
    folder_id?: string
    onboarding_folder_id?: number
    field_ids?: number[]
  }
}

const CONTRACT_CATEGORY = 'contract'

const allowedUploadFolderCategories = (): Partial<
  Record<
    DataCollectionCategories,
    {
      title: string
      description: string
    }
  >
> => ({
  [DataCollectionCategories.bankInformation]: {
    title: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.bank_certificate.title'
    ),
    description: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.bank_certificate.description'
    ),
  },
  [DataCollectionCategories.identificationNumber]: {
    title: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.title'
    ),
    description: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.description'
    ),
  },
})

const useFetchData = createAsyncData((contractVersionId: number) => {
  return Promise.all([
    contractVersionsPayrollPolicies.fetch({
      data: {
        contract_version_ids: [contractVersionId],
      },
    }),
    policies.fetch({ data: { include_archived: true } }),
    jobSalaryRangesCollection.fetch(),
  ])
})

const DataCollection = observer(
  ({
    task,
    canSeePersonalInfo,
    canEditPersonalInfo,
    canEmployeeManageAnyEmployeeFolder,
  }: Props) => {
    const [legalEntity, setLegalEntity] = React.useState<LegalEntity | null>(null)
    const configurationFields = JSON.parse(task.dataCollectionTypeData?.configurationFields || '')
    const employeeId = task.dataCollectionTypeData?.employeeId
    const employee = users.find({ id: task.affectees[0]?.id })?.employee
    const listOfFields = useLazyRef(() => observable.map(new Map())).current
    const { professionalsActivated, hiringIncidence, incidenceRules } = useEmployeeIncidenceRules(
      employee?.id
    )

    if (!employee) return null

    const { loading: loadingContractAndEntities } = useAsyncData(async () => {
      const employeeLegalEntity = await Promise.all([
        legalEntities.fetch({
          data: {
            employee_id: employee.id,
          },
          remove: false, // Don't remove the other LEs from the global collection
        }),
        contractVersions.fetch({
          data: {
            employee_ids: [employeeId],
          },
        }),
      ])

      if (Array.isArray(employeeLegalEntity)) setLegalEntity(employeeLegalEntity[0][0] || null)
    }, [employee.id])

    const contractVersion = useUnsafeMemo(() => {
      const today = moment()
      const current = filterContractVersionsByCurrent(contractVersions.models, today)[0]

      if (current) return current

      return getUpcomingContractVersions(contractVersions.models, today)
    }, [
      contractVersions.models,
      contractVersions.models.length,
      task.id,
      loadingContractAndEntities,
    ]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadingContractData = useFetchData(contractVersion?.id)
    const legalEntityContractData = useFetchLegalEntityContractData(
      employeeLegalEntityId(employee),
      employeeLegalEntity(employee).country
    )

    const {
      loading: contractDataLoading,
      spanishContractData,
      frenchContractData,
      germanContractData,
      portugueseContractData,
      commonContractData,
    } = legalEntityContractData

    if (contractDataLoading) return null

    const readonly = !canEditPersonalInfo
    const isEligibleForPayroll = employee.isEligibleForPayroll
    const isSpanish = employee.attributes.nationality === 'es'

    const hasCues =
      (!readonly && professionalsActivated && hiringIncidence && incidenceRules?.length > 0) ||
      false
    const categories: ICategory[] = configurationFields[0].categories
    const category = categories[0]?.category
    const hasFolder = categories[0]?.configuration?.folder_id
    const folderConfiguration = categories[0]?.configuration

    const isContractCategories = configurationFields[0].resource === CONTRACT_CATEGORY
    const contractFieldIds = isContractCategories
      ? categories.flatMap((cat) => cat.configuration?.field_ids).filter(Boolean)
      : undefined
    const fieldIds = categories[0]?.configuration?.field_ids?.filter(Boolean)

    const uploadDocumentText = category && allowedUploadFolderCategories()[category]
    const isIdentificationNumber = category === DataCollectionCategories.identificationNumber

    const showAttachments = employeeId && uploadDocumentText && hasFolder
    const showSocialAndId = category && isIdentificationNumber && employeeId

    const title = (cat: DataCollectionCategories) => {
      switch (cat) {
        case DataCollectionCategories.address:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.address.title'
          )
        case DataCollectionCategories.fiscal_residence:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.fiscal_residence.title'
          )
        case DataCollectionCategories.emergency_contact:
          return i18n.t('employee_form.emergency_contact.title')
        case DataCollectionCategories.generalInformation:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.general.title'
          )
        case DataCollectionCategories.personalSituation:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.personal_situation.title'
          )
        case DataCollectionCategories.start_date:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.start_date.title'
          )
        case DataCollectionCategories.role:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.role.title'
          )
        case DataCollectionCategories.salary:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.salary.title'
          )
        case DataCollectionCategories.contract_hours:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.contract_hours.title'
          )
        case DataCollectionCategories.contract_custom_fields:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.contract_custom_fields.title'
          )
        default:
          return ''
      }
    }

    const categoriesElements = () => {
      const orderedCategories = Object.values(DataCollectionCategories).filter((cat) => {
        return categories.find((c) => c.category === cat)
      })

      return orderedCategories.map((cat) => {
        return (
          <Box gap="s8" key={cat} paddingBottom="s8">
            <Text.Normal size="300" color="grey800" weight="medium">
              {title(cat)}
            </Text.Normal>
            <Box background="grey200" borderRadius={{ all: 'abs004' }} padding="s32">
              {isContractCategories ? (
                <ContractData legalEntity={legalEntity} category={cat} />
              ) : (
                <EmployeeData
                  employee={employee}
                  legalEntity={legalEntity}
                  isEligibleForPayroll={isEligibleForPayroll}
                  readonly={readonly}
                  fieldIds={fieldIds}
                  isSpanish={isSpanish}
                  category={cat}
                  canSeePersonalInfo={canSeePersonalInfo}
                  canEditPersonalInfo={canEditPersonalInfo}
                />
              )}
            </Box>
          </Box>
        )
      })
    }

    const contractVersionPayrollPolicy = contractVersionsPayrollPolicies.isEmpty
      ? null
      : contractVersionsPayrollPolicies.models[0]! // eslint-disable-line no-type-assertion/no-type-assertion

    if (loadingContractData) {
      return (
        <Box width="full" height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )
    }

    return (
      <ContractFlowProvider
        employee={employee}
        contractVersion={contractVersion}
        salaryRanges={jobSalaryRangesCollection.models}
        contractVersionPayrollPolicy={contractVersionPayrollPolicy}
        legalEntityContractData={legalEntityContractData}
      >
        <LegalEntityContractDataProvider
          spanishContractData={spanishContractData}
          frenchContractData={frenchContractData}
          germanContractData={germanContractData}
          portugueseContractData={portugueseContractData}
          commonContractData={commonContractData}
        >
          <ContractVersionsPayrollPoliciesProvider collection={contractVersionsPayrollPolicies}>
            <PoliciesProvider collection={policies}>
              <ContractVersionsProvider collection={contractVersions}>
                <FixFlowProvider fieldIds={contractFieldIds}>
                  <ContractCuesProvider
                    collection={{
                      hasCues: hasCues,
                      incidenceRules: incidenceRules,
                      listOfFields: listOfFields,
                    }}
                  >
                    <EmployeePersonalProvider
                      disableSectionSaveButtons
                      isContractCategories={isContractCategories}
                      employee={employee}
                    >
                      <Box gap="s24">
                        <Box>
                          <Text.Normal weight="semibold" color="grey1000">
                            {i18n.t(
                              'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.title'
                            )}
                          </Text.Normal>
                          <Text.Normal color="grey800">
                            {isContractCategories
                              ? i18n.t(
                                  'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.contract_description'
                                )
                              : i18n.t(
                                  'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.description'
                                )}
                          </Text.Normal>
                        </Box>
                        {canSeePersonalInfo ? (
                          <>
                            {<Box gap="s16">{categoriesElements()}</Box>}
                            <Footer />
                            {showAttachments && uploadDocumentText && !isIdentificationNumber && (
                              <>
                                <UploadDocuments
                                  task={task}
                                  title={uploadDocumentText.title}
                                  description={uploadDocumentText.description}
                                  employeeId={employeeId}
                                  canEmployeeManageAnyEmployeeFolder={
                                    canEmployeeManageAnyEmployeeFolder
                                  }
                                />
                              </>
                            )}
                            {showSocialAndId && (
                              <IDAndSocialSecurityUploads
                                folderConfiguration={folderConfiguration}
                                task={task}
                                employeeId={employeeId}
                                canEmployeeManageAnyEmployeeFolder={
                                  canEmployeeManageAnyEmployeeFolder
                                }
                              />
                            )}
                          </>
                        ) : (
                          <Warning />
                        )}
                      </Box>
                    </EmployeePersonalProvider>
                  </ContractCuesProvider>
                </FixFlowProvider>
              </ContractVersionsProvider>
            </PoliciesProvider>
          </ContractVersionsPayrollPoliciesProvider>
        </LegalEntityContractDataProvider>
      </ContractFlowProvider>
    )
  }
)

const DataCollectionWrapper: FC<Props> = ({
  task,
  canSeePersonalInfo,
  canEditPersonalInfo,
  canEmployeeManageAnyEmployeeFolder,
}) => {
  if (!canSeePersonalInfo) return <Warning />

  return (
    <DataCollection
      task={task}
      canSeePersonalInfo={canSeePersonalInfo}
      canEditPersonalInfo={canEditPersonalInfo}
      canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
    />
  )
}

export default observer(DataCollectionWrapper)
